import { Component, OnInit, inject } from '@angular/core';
import {
  IActividadUsuario,
  IFilter,
  IPopulate,
  IQueryParam,
} from 'modelos/src';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';
import {
  IFiltroTabla,
  IRegExpSearch,
} from '../../../../auxiliares/tabla/filtro/filtro.component';
import { IColumnas } from '../../../../auxiliares/tabla/tabla.component';
import { EstadisticasUsoService } from '../../../../auxiliares/servicios/http/estadisticas-uso.service';
import { ClienteJerarquiaService } from '../../../../auxiliares/servicios/cliente-jerarquia.service';

/**
 * Listado de registros de periodo de actividad de los usuarios del cliente (y
 * sus sub-clientes). El backend acota por el permiso del request y exige la
 * acción "Administración - Ver periodo actividad usuarios".
 */
@Component({
  selector: 'app-listado-periodo-actividad-usuarios',
  templateUrl: './listado-periodo-actividad-usuarios.component.html',
  standalone: false,
})
export class ListadoPeriodoActividadUsuariosComponent implements OnInit {
  helper = inject(HelperService);
  private clienteJerarquia = inject(ClienteJerarquiaService);
  private service = inject(EstadisticasUsoService);

  public name = 'ListadoPeriodoActividadUsuariosComponent';
  public datos?: IActividadUsuario[];
  public totalCount = 0;
  public columnas?: IColumnas<IActividadUsuario>[];

  public search: IRegExpSearch = {
    fields: ['usuario'],
  };

  private rango: IFiltroTabla = {
    filter: {
      field: 'inicio',
    },
    label: 'Rango de Fechas',
    tipo: 'dateRange',
    // Días completos, igual que el período de Estadísticas de uso.
    soloDia: true,
  };

  private usuario: IFiltroTabla = {
    filter: {
      field: 'usuario',
    },
    label: 'Usuario',
    tipo: 'regexp',
  };

  // FILTROS de cliente. Propietario = coincidencia exacta; jerarquía = ese
  // cliente y toda su descendencia (idsAncestros no incluye al propio).
  public cliente: IFiltroTabla = {
    filter: {
      field: 'idCliente',
    },
    label: 'Cliente',
    tipo: 'cliente-selector',
  };
  public ancestro: IFiltroTabla = {
    filter: {
      field: 'idsAncestros',
      incluirPropio: true,
    },
    label: 'En la jerarquía de',
    tipo: 'cliente-selector',
  };
  public filtros: IFiltroTabla[] = [this.rango, this.usuario];

  private filter: IFilter<IActividadUsuario> = {};
  private populate: IPopulate = [
    {
      path: 'cliente',
      select: 'nombre',
    },
  ];

  public query: IQueryParam = {
    filter: JSON.stringify(this.filter),
    populate: JSON.stringify(this.populate),
    sort: '-inicio',
    page: 0,
    limit: +this.helper.getPageSize(this.name),
    includeChildren: true,
    childrenLevel: 0,
  };

  public async listar(): Promise<void> {
    try {
      const data = await this.service.getPeriodoActividad(this.query);
      this.datos = data.datos;
      this.totalCount = data.totalCount ?? 0;
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
  }

  // Tabla

  private duracion(dato: IActividadUsuario): string {
    if (!dato.inicio || !dato.fin) return '';
    const ms = new Date(dato.fin).getTime() - new Date(dato.inicio).getTime();
    if (ms < 0) return '';
    const totalMin = Math.floor(ms / 60_000);
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    return h ? `${h} h ${m} min` : `${m} min`;
  }

  private setColumnas() {
    this.columnas = [
      // Usuario
      {
        header: {
          label: 'Usuario',
        },
        row: {
          field: 'usuario',
          noData: 'Sin usuario',
        },
      },
      // Cliente
      {
        header: {
          label: 'Cliente',
        },
        row: {
          html: (dato) => this.clienteJerarquia.htmlNombre(dato.cliente),
          tooltip: (dato) =>
            this.clienteJerarquia.tooltipJerarquia(dato.cliente),
          noData: 'Sin cliente',
        },
      },
      // Inicio del ciclo de uso
      {
        header: {
          label: 'Inicio de actividad',
        },
        row: {
          fecha: (dato) => dato.inicio,
          hace: true,
        },
      },
      // Aviso de descanso (fin del período de actividad continua)
      {
        header: {
          label: 'Aviso de descanso',
        },
        row: {
          fecha: (dato) => dato.fin,
          noData: 'Sin aviso',
        },
      },
      // Tiempo activo
      {
        header: {
          label: 'Tiempo activo',
        },
        row: {
          parse: (dato) => this.duracion(dato),
          noData: 'Sin duración',
        },
      },
    ];
  }

  // Hooks

  async ngOnInit() {
    if (HelperService.getTipoCliente() !== 'Final') {
      this.filtros.push(this.cliente);
      this.filtros.push(this.ancestro);
    }
    this.setColumnas();
    await this.listar();
  }
}
